import {
  Paper,
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
  styled,
} from "@mui/material";
import FileInput from "../../components/inputs/FileInput";
import { memo, useState } from "react";
import ImageComponent from "../../components/ImageComponent";
import {
  Delete,
  HideImageOutlined,
  PhotoCamera,
  Upload,
} from "@mui/icons-material";
import { CenteredBox } from "../../components/layouts/OneViewBox";
import { toTitleCase } from "../../utils/helper";
import FileDownloadComponent from "../../components/FileDownloadComponent";
import CameraCapture from "../../components/CameraCapture";
import MobileScanner from "../../components/Mobilescanner";
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FileViewer = memo(({ src, ...props }) => {
  const fileNameSplitted = src?.split(".");
  console.log("file Name splitted ", fileNameSplitted);
  const fileExtension = fileNameSplitted[fileNameSplitted.length - 1];
  console.log("file extensisons", fileExtension);
  if (fileExtension === "true") {
    return (
      <>
        <CenteredBox>
          <Typography variant="caption" textAlign="center">
            All Files are merged in one file.
          </Typography>
        </CenteredBox>
      </>
    );
  }
  if (
    fileExtension?.slice(0, 3) === "jpg" ||
    fileExtension?.slice(0, 4) === "jpeg" ||
    fileExtension?.slice(0, 3) === "png" ||
    fileExtension?.slice(0, 3) === "svg"
  )
    return (
      <ImageComponent
        imgStyle={{ height: "100%", width: "100%", objectFit: "contain" }}
        src={src}
      />
    );
  return (
    <CenteredBox sx={{ flexDirection: "column" }}>
      <FileDownloadComponent src={src} view />
      <Typography sx={{ wordBreak: "break-word" }}>Download</Typography>
    </CenteredBox>
  );
});

const ImageUploadComponent = memo(
  ({
    loading,
    onChange,
    percentage,
    onDeleteFile,
    files,
    name,
    onQRUploaded,
  }) => {
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);

    const onDelete = () => {
      setDeleteLoading(true);
      onDeleteFile(files[0], () => {
        setDeleteLoading(false);
      });
    };

    const handleCameraCapture = (file) => {
      // Build a proper FileList using DataTransfer so downstream
      // handlers that expect an <input type="file"> event work correctly.
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      const syntheticEvent = {
        target: {
          files: dataTransfer.files,
          value: "",
        },
        currentTarget: {
          files: dataTransfer.files,
        },
        preventDefault: () => {},
        stopPropagation: () => {},
      };

      onChange(syntheticEvent);
      setCameraOpen(false);
    };

    return (
      <Box
        component={Paper}
        sx={{ width: "100%", background: "#e5e5e5" }}
        pt={1}
      >
        <Typography variant="subtitle2" align="center" lineHeight="100%">
          {toTitleCase(name?.replace(/_/g, " "))}
        </Typography>
        <Box
          component={Paper}
          elevation={0}
          mt={1}
          mb={1}
          sx={{
            display: "flex",
            width: "100%",
            position: "relative",
            paddingTop: "100%",
            border: "1px solid gray",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "0px",
              left: "0px",
              right: "0px",
              width: "100%",
              height: "100%",
            }}
          >
            {files[0] && !loading && !deleteLoading ? (
              <FileViewer src={files[0]} />
            ) : (
              <CenteredBox>
                {loading ? (
                  <LinearProgress
                    sx={{ zIndex: 1111, width: "100%" }}
                    variant="determinate"
                    value={percentage}
                  />
                ) : deleteLoading ? (
                  <CircularProgress />
                ) : (
                  <HideImageOutlined />
                )}
              </CenteredBox>
            )}
          </Box>
        </Box>
        <Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {!files[0] && (
              <>
                <Button
                  component="label"
                  mt={2}
                  startIcon={<Upload />}
                  disableElevation
                  disabled={loading || deleteLoading}
                  variant="contained"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {/* Upload */}
                  <VisuallyHiddenInput
                    accept="image/*,application/pdf"
                    onChange={onChange}
                    type="file"
                  />
                </Button>
                <Button
                  mt={2}
                  disableElevation
                  disabled={loading || deleteLoading}
                  variant="contained"
                  color="primary"
                  onClick={() => setCameraOpen(true)}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <PhotoCamera />
                </Button>

                <MobileScanner onUploaded={onQRUploaded} />
              </>
            )}

            {files[0] && (
              <Button
                onClick={onDelete}
                component="label"
                color="error"
                mt={2}
                startIcon={<Delete />}
                disableElevation
                disabled={loading || deleteLoading}
                variant="contained"
                fullWidth
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>

        <CameraCapture
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={handleCameraCapture}
        />
      </Box>
    );
  },
);

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const DocumentFields = ({
  buttonDisable,
  setButtonDisable,
  show,
  fields,
  setFields,
  loading,
  setLoading,
}) => {
  const [uploadProgress, setUploadProgress] = useState({
    loading: false,
    loaded: 0,
    total: 0,
  });
  return (
    <>
      {uploadProgress.loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100% ",
            zIndex: 1300,
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgressWithLabel
            value={Math.round(
              (uploadProgress.loaded * 100) / uploadProgress.total,
            )}
          />
          <Typography>Please wait, document is being uploaded.</Typography>
        </Box>
      )}
      <Box sx={{ width: "100%", display: show ? "block" : "none" }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3}>
            <Box>
              {console.log(fields)}
              <FileInput
                disabled={loading}
                key={JSON.stringify(fields)}
                onlyImage
                // noCompression
                defaults={fields.photo ? [fields.photo] : undefined}
                componentProps={{
                  name: "Photo",
                  url: fields.photo,
                }}
                onPercentage={(loaded, total, loading) => {
                  setUploadProgress({ loaded, total, loading });
                }}
                onChange={(newUrl) => {
                  console.log("This is photo new url", newUrl);
                  if (newUrl) {
                    // setButtonDisable(false)
                    setFields({ ...fields, photo: newUrl });
                  } else {
                    setLoading(true);
                  }
                }}
                FileComponent={ImageUploadComponent}
                onDelete={() => {
                  setFields({ ...fields, photo: "" });
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Box>
              <FileInput
                key={JSON.stringify(fields)}
                disabled={loading}
                onlyImage
                noCompression={false}
                defaults={fields.aadhar_file ? [fields.aadhar_file] : undefined}
                componentProps={{
                  name: "Aadhar Card",
                  url: fields.aadhar_file,
                }}
                onPercentage={(loaded, total, loading) => {
                  setUploadProgress({ loaded, total, loading });
                }}
                onChange={(newUrl) => {
                  console.log("This is Adddhar new url", newUrl);
                  if (newUrl) {
                    setFields({ ...fields, aadhar_file: newUrl });
                    // setButtonDisable(false);
                    // setFields({ ...fields, photo: newUrl })
                  } else {
                    setLoading(true);
                  }
                }}
                FileComponent={ImageUploadComponent}
                onDelete={() => {
                  setFields({ ...fields, aadhar_file: "" });
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Box>
              <FileInput
                disabled={loading}
                key={JSON.stringify(fields)}
                onlyImage
                noCompression={false}
                defaults={fields.udise_file ? [fields.udise_file] : undefined}
                componentProps={{
                  name: "Disablity Certificate",
                  url: fields.udise_file,
                }}
                onPercentage={(loaded, total, loading) => {
                  setUploadProgress({ loaded, total, loading });
                }}
                onChange={(newUrl) => {
                  console.log("This is Disibility file new url", newUrl);
                  if (newUrl) {
                    // setFields({ ...fields, photo: newUrl })
                    setFields({ ...fields, udise_file: newUrl });
                  } else {
                    setLoading(true);
                  }
                }}
                FileComponent={ImageUploadComponent}
                onDelete={() => {
                  setFields({ ...fields, udise_file: "" });
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Box>
              <FileInput
                key={JSON.stringify(fields)}
                disabled={loading}
                onlyImage
                noCompression={false}
                defaults={
                  fields.income_certi ? [fields.income_certi] : undefined
                }
                onPercentage={(loaded, total, loading) => {
                  setUploadProgress({ loaded, total, loading });
                }}
                componentProps={{
                  name: "Income Certificate",
                  url: fields.income_certi,
                }}
                onChange={(newUrl) => {
                  console.log("This is Income new url", newUrl);
                  if (newUrl) {
                    setFields({ ...fields, income_certi: newUrl });
                    // setFields({ ...fields, photo: newUrl })
                  } else {
                    setLoading(true);
                  }
                }}
                FileComponent={ImageUploadComponent}
                onDelete={() => {
                  setFields({ ...fields, income_certi: "" });
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default DocumentFields;
